import { useCallback, useState } from 'react';

/**
 * Which facet the folder-tab strip above the table files by, remembered per module.
 *
 * The page declares a default — `folderNavFacet`, `status` unless it says otherwise — and the
 * reader repoints it from the filter rail's cog. A module carries more axes than any one
 * reader cuts by, and which of them is worth one click rather than two through the rail is a
 * property of the person reading, not of the module: someone staffing posts files personnel
 * by employment, someone auditing files them by status, and the page cannot know which.
 *
 * **It is an arrangement, not a filter, which is what makes it safe to persist.** It decides
 * how the same records are presented and never which of them qualify — `useTreeOrientation`'s
 * distinction exactly, and the opposite of Show deleted, where a remembered preference is how
 * a deleted row comes to be treated as live. Repointing the strip changes no facet selection:
 * a filter the reader left on the previous axis stays on, stays applied, and stays visible in
 * the rail, because a layout control that silently cleared a filter would be destructive —
 * the reasoning `useFacetVisibility` already runs on.
 *
 * **The stored key is a preference, and the resolved key is derived from it.** A facet can
 * leave the page, be renamed, or sit behind a permission this reader does not hold; any of
 * those would leave a stored key naming nothing, and the strip would simply not render —
 * silently, which is the failure mode worth designing out. So the stored value is honoured
 * only while it is among the facets actually on offer, and the page's own default is what it
 * falls back to. Derived on every render rather than reconciled in an effect, so it cannot be
 * one render stale.
 */
function storageKey(module: string): string {
  return `index.${module}.folderNavFacet`;
}

export function useFolderNavFacet(module: string, fallback: string, available: string[]) {
  const [stored, setStored] = useState<string | null>(() => {
    try {
      return localStorage.getItem(storageKey(module));
    } catch {
      // Blocked site data — the page's own default still works.
      return null;
    }
  });

  /*
    The resolved axis. `available` is what the reader can actually be offered — the
    permission-filtered facets — so a stored key for a facet they may not see falls back
    rather than putting a gated axis's values in tabs across the top of the table.
  */
  const facet = stored !== null && available.includes(stored) ? stored : fallback;

  const setFacet = useCallback(
    (next: string) => {
      setStored(next);

      try {
        localStorage.setItem(storageKey(module), next);
      } catch {
        // Remembering the preference is a convenience, never a requirement.
      }
    },
    [module],
  );

  return { facet, setFacet };
}
